import { useState, useCallback, useEffect } from "react"
import type { VisibilityState } from "@tanstack/react-table"

const STORAGE_PREFIX = "okeycrm:columns:"

/**
 * Hook to manage column visibility preferences with localStorage persistence.
 *
 * @param entityKey - unique key per list (e.g. "companies", "contacts")
 * @param allColumnIds - all available column IDs
 * @param defaultVisibleIds - which columns are visible by default
 */
export function useColumnPreferences(
  entityKey: string,
  allColumnIds: string[],
  defaultVisibleIds: string[]
) {
  const storageKey = `${STORAGE_PREFIX}${entityKey}`

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        return JSON.parse(stored) as VisibilityState
      }
    } catch {
      // ignore parse errors
    }

    // Build default visibility: columns not in defaultVisibleIds are hidden
    const visibility: VisibilityState = {}
    for (const id of allColumnIds) {
      if (!defaultVisibleIds.includes(id)) {
        visibility[id] = false
      }
    }
    return visibility
  })

  // Persist to localStorage whenever visibility changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columnVisibility))
    } catch {
      // ignore quota errors
    }
  }, [storageKey, columnVisibility])

  const visibleCount = allColumnIds.filter((id) => columnVisibility[id] !== false).length
  const totalCount = allColumnIds.length

  const toggleColumn = useCallback((columnId: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: prev[columnId] === false ? true : false,
    }))
  }, [])

  const showAll = useCallback(() => {
    const visibility: VisibilityState = {}
    for (const id of allColumnIds) {
      visibility[id] = true
    }
    setColumnVisibility(visibility)
  }, [allColumnIds])

  const hideAll = useCallback(() => {
    const visibility: VisibilityState = {}
    for (const id of allColumnIds) {
      visibility[id] = false
    }
    setColumnVisibility(visibility)
  }, [allColumnIds])

  const resetDefaults = useCallback(() => {
    const visibility: VisibilityState = {}
    for (const id of allColumnIds) {
      if (!defaultVisibleIds.includes(id)) {
        visibility[id] = false
      }
    }
    setColumnVisibility(visibility)
  }, [allColumnIds, defaultVisibleIds])

  const isColumnVisible = useCallback(
    (columnId: string) => columnVisibility[columnId] !== false,
    [columnVisibility]
  )

  return {
    columnVisibility,
    setColumnVisibility,
    visibleCount,
    totalCount,
    toggleColumn,
    showAll,
    hideAll,
    resetDefaults,
    isColumnVisible,
  }
}
